// Zero-dependency, client-side export of ONE conversation (session) to a
// CSV that opens natively in Excel / Google Sheets — so an admin can follow a
// chat step by step and keep all of its data. The full transcript is already
// loaded in the Session Inspector (`SessionDetailResponse`), so the export
// reuses that in-memory data with no extra request.
//
// Why CSV and not .xlsx: a real .xlsx workbook needs a third-party library
// (sheetjs/exceljs) — a supply-chain cost we avoid here. Excel opens a
// UTF-8-BOM CSV directly; the BOM is what makes Swiss umlauts (ä/ö/ü) render.

import type { SessionDetailResponse, TurnDetail } from "@/api/types";
import { t, type TranslationKey } from "@/i18n";
import { formatDateTime, modeLabel } from "@/lib/format";
import { depthValue } from "@/lib/reasoningDepth";

// UTF-8 byte-order mark (U+FEFF). Prefixing the CSV with it makes Excel detect
// UTF-8 (otherwise it falls back to a legacy codepage and mangles ä/ö/ü).
const BOM = String.fromCharCode(0xfeff);

// Characters that make a spreadsheet evaluate a cell instead of showing it:
// formula prefixes (= + - @) and the control characters Excel treats as a
// field break. The question and answer columns carry text typed by anonymous
// chat users, so a cell starting with `=HYPERLINK(...)` would execute on the
// admin's machine the moment the export opens.
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

/** Quote a value for one CSV cell: wrap in double-quotes and double any
 * internal quote. Always-quote so commas, semicolons and newlines inside the
 * text never break the column structure (a quoted cell may legally contain
 * newlines — Excel renders it as a multi-line cell). A cell that starts with
 * a formula prefix gets a leading apostrophe so it stays text. null/undefined
 * → "". */
export function csvCell(value: unknown): string {
  const raw = value === null || value === undefined ? "" : String(value);
  const s = FORMULA_PREFIX.test(raw) ? `'${raw}` : raw;
  return `"${s.replace(/"/g, '""')}"`;
}

/** One CSV line of `cells`, each quoted by `csvCell`. */
export function csvRow(cells: readonly unknown[]): string {
  return cells.map(csvCell).join(",");
}

/** A yes/no cell in the dashboard language. */
export const yesNo = (value: boolean): string => (value ? t("common.yes") : t("common.no"));

/** "Title — url" per retrieved chunk, one per line inside the cell. Reads the
 * loosely-typed chunk dict defensively (older turns may omit title). */
function sourcesText(turn: TurnDetail): string {
  return turn.retrieved_chunks
    .map((c) => {
      const url = typeof c.source_url === "string" ? c.source_url : "";
      const title = typeof c.title === "string" ? c.title : "";
      return [title, url].filter(Boolean).join(" — ");
    })
    .filter(Boolean)
    .join("\n");
}

/** The depth a turn ran with, in the provider's wording («Modell-Standard»
 * when none was sent); empty on a turn stored before the provider was. */
function turnDepth(turn: TurnDetail): string {
  if (!turn.provider) {
    return "";
  }
  return depthValue({ provider: turn.provider, effort: turn.reasoning_depth ?? "" });
}

// One column per turn field. Headers follow the dashboard language. The
// order reads as a transcript: who/when/what first, then grounding + signals.
// The accessor parameter is `turn`, never `t` — `t` is the translate function
// this module imports, and shadowing it would silently break the next header
// or label added inside one of these lambdas.
const TURN_COLUMNS: Array<[TranslationKey, (turn: TurnDetail, index: number) => unknown]> = [
  ["export.col.nr", (_turn, i) => i + 1],
  ["export.col.time", (turn) => formatDateTime(turn.timestamp)],
  ["export.col.mode", (turn) => modeLabel(turn.chat_mode)],
  ["export.col.model", (turn) => turn.model_used],
  // How the model ran; both empty on a turn stored before they were recorded.
  ["export.col.provider", (turn) => turn.provider ?? ""],
  ["export.col.depth", (turn) => turnDepth(turn)],
  ["export.col.question", (turn) => turn.user_query],
  ["export.col.answer", (turn) => turn.assistant_response],
  ["export.col.sources", (turn) => sourcesText(turn)],
  ["export.col.feedback", (turn) => turn.feedback ?? ""],
  ["export.col.feedbackComment", (turn) => turn.feedback_comment ?? ""],
  ["export.col.intent", (turn) => turn.question_intent],
  ["export.col.berufsfeld", (turn) => turn.berufsfeld],
  ["export.col.mentions", (turn) => turn.mentions.join(", ")],
  ["export.col.affect", (turn) => turn.user_affect.join(", ")],
  ["export.col.traits", (turn) => turn.traits.join(", ")],
  ["export.col.inputTokens", (turn) => turn.input_tokens],
  ["export.col.outputTokens", (turn) => turn.output_tokens],
  ["export.col.cacheHit", (turn) => yesNo(turn.cache_hit)],
  [
    "export.col.guardrail",
    (turn) => (turn.guardrail_triggered ? (turn.guardrail_reason ?? yesNo(true)) : ""),
  ],
  ["export.col.streamOk", (turn) => yesNo(turn.streaming_completed)],
  ["export.col.hits", (turn) => `${turn.chunks_retrieved}/${turn.chunks_used}`],
  ["export.col.maxRelevance", (turn) => turn.max_chunk_relevance_score],
  // Retrieval funnel of the turn: found → forwarded → cited, filler the score
  // floor removed, characters the model read. Optional on older backends.
  ["export.col.docsFound", (turn) => turn.docs_returned ?? ""],
  ["export.col.pagesForwarded", (turn) => turn.chunks_used],
  ["export.col.sourcesCited", (turn) => turn.sources_cited ?? ""],
  ["export.col.belowFloor", (turn) => turn.docs_below_floor ?? ""],
  ["export.col.contextChars", (turn) => turn.context_chars ?? ""],
  // What retrieval was asked and anchored on, and how long the answer took.
  // Optional on older backends.
  ["export.col.retrievalTopic", (turn) => turn.retrieval_topic ?? ""],
  ["export.col.searchQuery", (turn) => turn.search_query ?? ""],
  ["export.col.ttft", (turn) => turn.ttft_ms ?? ""],
  ["export.col.latency", (turn) => turn.total_latency_ms ?? ""],
  ["export.col.turnId", (turn) => turn.turn_id],
];

function metadataRows(detail: SessionDetailResponse): Array<[string, string]> {
  const s = detail.session;
  return [
    [t("sessions.meta.id"), s.session_id],
    [t("sessions.meta.created"), formatDateTime(s.created_at)],
    [t("sessions.meta.updated"), formatDateTime(s.updated_at)],
    [t("sessions.meta.locale"), s.locale],
    [t("sessions.meta.country"), s.country],
    [t("sessions.meta.device"), s.device],
    [t("sessions.meta.mode"), modeLabel(s.chat_mode)],
    [t("sessions.meta.startedModes"), s.started_modes.map(modeLabel).join(" → ")],
    [t("sessions.meta.turns"), String(s.turn_count)],
    [t("sessions.meta.questions"), String(s.question_count)],
    [t("sessions.meta.isTest"), yesNo(s.is_test)],
    [t("sessions.meta.title"), s.title ?? ""],
  ];
}

/** Build the single-conversation CSV: a 2-column session-metadata block, a
 * blank separator row, then the full turn-by-turn table (one row per turn,
 * every field). `\r\n` row endings + always-quoted cells so Excel parses it
 * cleanly regardless of commas / newlines in the text. The UTF-8 BOM is added
 * by `downloadCsv` (kept out here so the string stays assertable). */
export function buildSessionCsv(detail: SessionDetailResponse): string {
  const lines = metadataRows(detail).map(csvRow);
  lines.push(""); // blank separator between the metadata block and the table
  lines.push(csvRow(TURN_COLUMNS.map(([header]) => t(header))));
  detail.turns.forEach((turn, i) => {
    lines.push(csvRow(TURN_COLUMNS.map(([, get]) => get(turn, i))));
  });
  return lines.join("\r\n");
}

/** File name for the export — `chat-<session-id>.csv`. */
export function sessionCsvFilename(detail: SessionDetailResponse): string {
  return `chat-${detail.session.session_id}.csv`;
}

/** Trigger a client-side download of a CSV that Excel opens as UTF-8: the BOM
 * is prepended here, so every builder returns a plain, assertable string. The
 * one download path of the session, cost and Fragen-Check exports. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the conversation as an Excel-openable CSV. */
export function downloadSessionCsv(detail: SessionDetailResponse): void {
  downloadCsv(sessionCsvFilename(detail), buildSessionCsv(detail));
}
